import React, { useCallback, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useUnit } from 'effector-react';

import { RemoveScheduleDialog } from './dialogs/RemoveScheduleDialog';
import { ScheduleAddDialog } from './dialogs/ScheduleAddDialog';
import { ScheduleAddWeekendDialog } from './dialogs/ScheduleAddWeekendDialog';
import { ScheduleEditDialog } from './dialogs/ScheduleEditDialog';
import { ScheduleRemoveWeekendDialog } from './dialogs/ScheduleRemoveWeekendDialog';
import {
  ScheduleAddDialogData,
  ScheduleAddWeekendDialogData,
  ScheduleEditDialogData,
  ScheduleRemoveDialogData,
} from './dialogs/types';
import { ProtectedComponent } from '../../components/ProtectedComponent';
import { codes$ } from '../../effector/classCodes';
import { forbidden$ } from '../../effector/forbidden';
import { schedule$ } from '../../effector/schedule';
import { ScheduleNames, SecurityInfo, TradeTime, Weekend } from '../../types/marketSchedule';
import { UserAction } from '../../utils/permissions';
import { Nullable } from '../../utils/types';

const sectionTitles: Record<ScheduleNames, string> = {
  [ScheduleNames.trade]: 'Рабочее время',
  [ScheduleNames.signal]: 'Принимаем сигналы от управляющего',
  [ScheduleNames.rebalance]: 'Время балансировки',
};

type TimeSectionProps = {
  scheduleId: number;
  type: ScheduleNames;
  markets: string[];
  forbiddenSecurities: SecurityInfo[];
  intervals: Nullable<TradeTime[]>;
  onEdit: (
    id: number,
    name: ScheduleNames,
    markets: string[],
    forbiddenSecurities: SecurityInfo[],
    intervals: Nullable<TradeTime[]>,
  ) => void;
};

const TimeSection: React.FC<TimeSectionProps> = ({
  scheduleId,
  type,
  markets,
  forbiddenSecurities,
  intervals,
  onEdit,
}) => (
  <Stack spacing={1.5}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      spacing={1}
    >
      <Typography variant="subtitle1" fontWeight={700}>
        {sectionTitles[type]}
      </Typography>
      <ProtectedComponent userAction={UserAction.scheduleEdit}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => onEdit(scheduleId, type, markets, forbiddenSecurities, intervals)}
        >
          Редактировать
        </Button>
      </ProtectedComponent>
    </Stack>

    {intervals?.length ? (
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small">
          <TableBody>
            {intervals.map((interval, index) => (
              <TableRow key={`${type}-${index}`}>
                <TableCell sx={{ width: 220, fontWeight: 600 }}>{`${interval.start} - ${interval.end}`}</TableCell>
                <TableCell>{interval.description || 'Без описания'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Alert severity="info">Интервалы не настроены.</Alert>
    )}
  </Stack>
);

export const Schedule: React.FC = () => {
  const allTimes = useUnit(schedule$);
  const allCodes = useUnit(codes$);
  const allForbidden = useUnit(forbidden$);
  const allMarkets = allCodes.map((code) => ({ id: code, name: code }));

  const [addDialog, setAddDialog] = useState<ScheduleAddDialogData | null>(null);
  const [editDialog, setEditDialog] = useState<ScheduleEditDialogData | null>(null);
  const [addWeekendDialog, setAddWeekendDialog] = useState<ScheduleAddWeekendDialogData | null>(null);
  const [removeDialog, setRemoveDialog] = useState<ScheduleRemoveDialogData | null>(null);
  const [removeWeekendDialog, setRemoveWeekendDialog] = useState<Weekend | null>(null);

  const handleCloseDialogs = useCallback(() => {
    setAddDialog(null);
    setEditDialog(null);
    setAddWeekendDialog(null);
    setRemoveDialog(null);
    setRemoveWeekendDialog(null);
  }, []);

  const onEdit = (
    id: number,
    name: ScheduleNames,
    markets: string[],
    forbiddenSecurities: SecurityInfo[],
    intervals: Nullable<TradeTime[]>,
  ) => {
    if (!intervals) return;

    setEditDialog({
      id,
      title: name,
      markets: [...markets],
      forbiddenSecurities,
      availableMarkets: allMarkets,
      times: [...intervals],
    });
  };

  const onAdd = () => {
    setAddDialog({
      availableMarkets: allMarkets,
    });
  };

  const onAddWeekend = (id: number) => {
    const weekends = allTimes.find((schedule) => schedule.id === id)?.weekends?.map((weekend) => weekend.day) ?? [];

    setAddWeekendDialog({
      id,
      weekends,
    });
  };

  const onRemove = (id: number, name: string) => {
    setRemoveDialog({ id, name });
  };

  const onRemoveWeekend = (id: number, day: string) => {
    setRemoveWeekendDialog({ id, day });
  };

  if (!allTimes) {
    return <Alert severity="warning">Расписание недоступно.</Alert>;
  }

  return (
    <>
      <Stack spacing={3}>
        <ProtectedComponent userAction={UserAction.scheduleEdit}>
          <Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
              Добавить расписание
            </Button>
          </Box>
        </ProtectedComponent>

        {allTimes.map((schedule) => {
          const title = `${schedule.name} (режимы торгов ${schedule.classCodes.join(', ')})`;

          return (
            <Paper key={schedule.id} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', lg: 'center' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack spacing={1.5}>
                    <Typography variant="h6" fontWeight={700}>
                      {title}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {schedule.classCodes.map((code) => (
                        <Chip key={code} label={code} variant="outlined" size="small" />
                      ))}
                    </Stack>
                  </Stack>
                  <ProtectedComponent userAction={UserAction.scheduleEdit}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => onRemove(schedule.id, schedule.name ?? '')}
                    >
                      Удалить расписание
                    </Button>
                  </ProtectedComponent>
                </Stack>

                <TimeSection
                  scheduleId={schedule.id}
                  type={ScheduleNames.trade}
                  markets={schedule.classCodes}
                  forbiddenSecurities={allForbidden}
                  intervals={schedule.tradeTimes}
                  onEdit={onEdit}
                />

                <Divider />

                <TimeSection
                  scheduleId={schedule.id}
                  type={ScheduleNames.signal}
                  markets={schedule.classCodes}
                  forbiddenSecurities={allForbidden}
                  intervals={schedule.signalTimes}
                  onEdit={onEdit}
                />

                <Divider />

                <TimeSection
                  scheduleId={schedule.id}
                  type={ScheduleNames.rebalance}
                  markets={schedule.classCodes}
                  forbiddenSecurities={allForbidden}
                  intervals={schedule.rebalanceTimes}
                  onEdit={onEdit}
                />

                <Divider />

                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      Праздничные и иные неторговые дни
                    </Typography>
                    <ProtectedComponent userAction={UserAction.scheduleEdit}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => onAddWeekend(schedule.id)}
                      >
                        Добавить день
                      </Button>
                    </ProtectedComponent>
                  </Stack>

                  {schedule.weekends?.length ? (
                    <Stack spacing={1}>
                      {schedule.weekends.map((weekend) => (
                        <Paper
                          key={`${weekend.id}-${weekend.day}`}
                          variant="outlined"
                          sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'background.default' }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <EventBusyOutlinedIcon color="error" fontSize="small" />
                              <Typography fontWeight={600}>{weekend.day} года нерабочий</Typography>
                            </Stack>
                            <ProtectedComponent userAction={UserAction.scheduleEdit}>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => onRemoveWeekend(weekend.id, weekend.day)}
                              >
                                Удалить
                              </Button>
                            </ProtectedComponent>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Нет добавленных неторговых дней.</Alert>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {addDialog && <ScheduleAddDialog availableMarkets={addDialog.availableMarkets} onClose={handleCloseDialogs} />}
      {editDialog && (
        <ScheduleEditDialog data={editDialog} forbiddenStore={allForbidden} onClose={handleCloseDialogs} />
      )}
      {addWeekendDialog && (
        <ScheduleAddWeekendDialog
          id={addWeekendDialog.id}
          weekends={addWeekendDialog.weekends}
          onClose={handleCloseDialogs}
        />
      )}
      {removeDialog && (
        <RemoveScheduleDialog id={removeDialog.id} name={removeDialog.name} onClose={handleCloseDialogs} />
      )}
      {removeWeekendDialog && (
        <ScheduleRemoveWeekendDialog weekend={removeWeekendDialog} onClose={handleCloseDialogs} />
      )}
    </>
  );
};
